import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuthentication } from './useAuthentication';
import {
  UserFavorite,
  UserFavoriteInput,
  UserFavoriteFilter,
  UserFavoriteCheckResponse,
  UserFavoritePayload,
  UseUserFavoritesReturn
} from '../types/userFavorites';
import { UserActivityEvent } from '../types/userActivity';
import { userFavoritesService } from '../services/userFavoritesService';
import { mergeSessionIntoHistory, computeInsights, getEffectiveSortDate, deduplicateByUrl } from '../utils/favoritesUtils';
import { detectKindFromUrl } from '../utils/urlUtils';

/**
 * Hook for managing user favorites
 * 
 * Unlike useUserTimeline, this hook:
 * - Has no session association
 * - Uses hard delete (permanent)
 * - Has no expiration/TTL
 * - Has no status tracking
 */
export const useUserFavorites = (): UseUserFavoritesReturn => {
  const { isAuthenticated } = useAuthentication();
  const hasLoadedInitial = useRef(false);

  // Core state
  const [favorites, setFavorites] = useState<UserFavorite[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  
  // Local check cache for instant UI feedback
  const [favoriteChecks, setFavoriteChecks] = useState<Map<string, UserFavoriteCheckResponse>>(new Map());

  // Helper to generate cache key
  const getCacheKey = useCallback((kind: string, itemId: string): string => {
    return `${kind}:${itemId}`;
  }, []);

  /**
   * Create a new favorite
   */
  const createFavorite = useCallback(async (input: UserFavoriteInput): Promise<UserFavorite> => {
    setCreating(true);
    setError(null);

    try {
      const favorite = await userFavoritesService.createFavorite(input);
      
      // Update local state
      setFavorites(prev => [favorite, ...prev]);
      
      // Update local check cache
      const checkKey = getCacheKey(favorite.kind, favorite.payload.itemId);
      setFavoriteChecks(prev => {
        const next = new Map(prev);
        next.set(checkKey, { isFavorited: true, favoriteId: favorite.id });
        return next;
      });

      return favorite;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create favorite';
      setError(message);
      throw err;
    } finally {
      setCreating(false);
    }
  }, [getCacheKey]);

  /**
   * Update an existing favorite
   */
  const updateFavorite = useCallback(async (id: string, payload: UserFavoritePayload): Promise<UserFavorite> => {
    setUpdating(true);
    setError(null);

    try {
      const updated = await userFavoritesService.updateFavorite(id, payload);
      
      // Update local state
      setFavorites(prev => prev.map(f => f.id === id ? updated : f));

      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update favorite';
      setError(message);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  /**
   * Delete a favorite (permanent)
   */
  const deleteFavorite = useCallback(async (id: string): Promise<void> => {
    setError(null);

    try {
      // Get the favorite before deleting for cache update
      const favorite = favorites.find(f => f.id === id);
      
      await userFavoritesService.deleteFavorite(id);
      
      // Update local state
      setFavorites(prev => prev.filter(f => f.id !== id));
      
      // Update local check cache
      if (favorite) {
        const checkKey = getCacheKey(favorite.kind, favorite.payload.itemId);
        setFavoriteChecks(prev => {
          const next = new Map(prev);
          next.set(checkKey, { isFavorited: false, favoriteId: null });
          return next;
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete favorite';
      setError(message);
      throw err;
    }
  }, [favorites, getCacheKey]);

  /**
   * Load favorites with optional filter
   */
  const loadFavorites = useCallback(async (filter?: UserFavoriteFilter): Promise<void> => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const response = await userFavoritesService.getFavorites(filter);
      
      setFavorites(response.items);
      setHasMore(response.hasMore);
      setContinuationToken(response.continuationToken || null);
      
      // Update local check cache
      const checks = new Map<string, UserFavoriteCheckResponse>();
      response.items.forEach(item => {
        const key = getCacheKey(item.kind, item.payload.itemId);
        checks.set(key, { isFavorited: true, favoriteId: item.id });
      });
      setFavoriteChecks(checks);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load favorites';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getCacheKey]);

  /**
   * Load more favorites (pagination)
   */
  const loadMore = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !hasMore || loadingMore || !continuationToken) return;

    setLoadingMore(true);
    setError(null);

    try {
      const response = await userFavoritesService.getFavorites({
        continuationToken
      });
      
      setFavorites(prev => [...prev, ...response.items]);
      setHasMore(response.hasMore);
      setContinuationToken(response.continuationToken || null);
      
      // Update local check cache
      response.items.forEach(item => {
        const key = getCacheKey(item.kind, item.payload.itemId);
        setFavoriteChecks(prev => {
          const next = new Map(prev);
          next.set(key, { isFavorited: true, favoriteId: item.id });
          return next;
        });
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load more favorites';
      setError(message);
    } finally {
      setLoadingMore(false);
    }
  }, [isAuthenticated, hasMore, loadingMore, continuationToken, getCacheKey]);

  /**
   * Refresh favorites
   */
  const refresh = useCallback(async (): Promise<void> => {
    userFavoritesService.clearCache();
    setHasMore(true);
    setContinuationToken(null);
    await loadFavorites();
  }, [loadFavorites]);

  /**
   * Get a single favorite by ID
   */
  const getFavorite = useCallback(async (id: string): Promise<UserFavorite> => {
    return userFavoritesService.getFavorite(id);
  }, []);

  /**
   * Check if an item is favorited
   */
  const checkFavorite = useCallback(async (kind: string, itemId: string): Promise<UserFavoriteCheckResponse> => {
    const result = await userFavoritesService.checkFavorite(kind, itemId);
    
    // Update local cache
    const checkKey = getCacheKey(kind, itemId);
    setFavoriteChecks(prev => {
      const next = new Map(prev);
      next.set(checkKey, result);
      return next;
    });
    
    return result;
  }, [getCacheKey]);

  /**
   * Check if an item is favorited (sync, from local cache)
   */
  const isFavorited = useCallback((kind: string, itemId: string): boolean => {
    const checkKey = getCacheKey(kind, itemId);
    const cached = favoriteChecks.get(checkKey);
    
    if (cached) {
      return cached.isFavorited;
    }
    
    // Check in loaded favorites
    return favorites.some(f => f.kind === kind && f.payload.itemId === itemId);
  }, [favoriteChecks, favorites, getCacheKey]);

  /**
   * Get favorites count
   */
  const getCount = useCallback(async (kind?: string): Promise<number> => {
    return userFavoritesService.getCount(kind);
  }, []);

  /**
   * Toggle the isFavorited flag on an existing favorite record.
   * Flips metadata.isFavorited and persists via updateFavorite.
   */
  const toggleFavoriteFlag = useCallback(async (id: string): Promise<{ isFavorited: boolean }> => {
    const existing = favorites.find(f => f.id === id);
    if (!existing) throw new Error(`Favorite ${id} not found`);

    const currentFlag = existing.payload?.metadata?.isFavorited === true;
    const newFlag = !currentFlag;

    const updatedPayload: UserFavoritePayload = {
      ...existing.payload,
      metadata: {
        ...existing.payload.metadata,
        isFavorited: newFlag,
      },
    };

    // Optimistic update
    setFavorites(prev => prev.map(f =>
      f.id === id
        ? { ...f, payload: updatedPayload }
        : f
    ));

    try {
      const updated = await userFavoritesService.updateFavorite(id, updatedPayload);
      setFavorites(prev => prev.map(f => f.id === id ? updated : f));
      return { isFavorited: newFlag };
    } catch (err) {
      // Revert optimistic update on failure
      setFavorites(prev => prev.map(f => f.id === id ? existing : f));
      const message = err instanceof Error ? err.message : 'Failed to toggle favorite flag';
      setError(message);
      throw err;
    }
  }, [favorites]);

  /**
   * Toggle favorite status — toggles the isFavorited metadata flag.
   * Creates the record if it doesn't exist; never deletes.
   */
  const toggleFavorite = useCallback(async (
    kind: string,
    itemId: string,
    itemData?: Partial<UserFavoritePayload>
  ): Promise<{ isFavorited: boolean; favoriteId: string | null }> => {
    setError(null);

    const checkKey = getCacheKey(kind, itemId);
    const currentStatus = favoriteChecks.get(checkKey);

    try {
      if (currentStatus?.isFavorited && currentStatus.favoriteId) {
        // Record exists — toggle the flag
        const existing = favorites.find(f => f.id === currentStatus.favoriteId);
        if (existing) {
          const currentFlag = existing.payload?.metadata?.isFavorited === true;
          const newFlag = !currentFlag;
          const updatedPayload: UserFavoritePayload = {
            ...existing.payload,
            metadata: {
              ...existing.payload.metadata,
              isFavorited: newFlag,
            },
          };
          // Optimistic update
          setFavorites(prev => prev.map(f =>
            f.id === existing.id ? { ...f, payload: updatedPayload } : f
          ));
          try {
            const updated = await userFavoritesService.updateFavorite(existing.id, updatedPayload);
            setFavorites(prev => prev.map(f => f.id === existing.id ? updated : f));
          } catch (err) {
            // Revert on failure
            setFavorites(prev => prev.map(f => f.id === existing.id ? existing : f));
            throw err;
          }
          return { isFavorited: newFlag, favoriteId: existing.id };
        }
      }

      // No record — create with isFavorited: true
      const payload: UserFavoritePayload = {
        itemId,
        title: itemData?.title || itemId,
        ...itemData,
        metadata: {
          ...itemData?.metadata,
          isFavorited: true,
        },
      };
      const favorite = await createFavorite({ kind, payload });
      return { isFavorited: true, favoriteId: favorite.id };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to toggle favorite';
      setError(message);
      throw err;
    }
  }, [getCacheKey, favoriteChecks, favorites, createFavorite]);

  /**
   * Filter favorites by kind (local)
   */
  const filterByKind = useCallback((kind: string): UserFavorite[] => {
    return favorites.filter(f => f.kind === kind);
  }, [favorites]);

  /**
   * Get favorite by ID (local)
   */
  const getFavoriteById = useCallback((id: string): UserFavorite | undefined => {
    return favorites.find(f => f.id === id);
  }, [favorites]);

  /**
   * Get favorite by kind and itemId (local)
   */
  const getFavoriteByItemId = useCallback((kind: string, itemId: string): UserFavorite | undefined => {
    return favorites.find(f => f.kind === kind && f.payload.itemId === itemId);
  }, [favorites]);

  /**
   * Get favorite by URL (local)
   */
  const getResourceByUrl = useCallback((url: string): UserFavorite | undefined => {
    return favorites.find(f => f.payload.url === url);
  }, [favorites]);

  /**
   * Record a resource viewing session.
   * Creates or updates a favorite record with history metadata.
   */
  const recordResourceVisit = useCallback(async (event: UserActivityEvent): Promise<void> => {
    if (!isAuthenticated || !event.url) return;

    try {
      const existing = favorites.find(f => f.payload.url === event.url);

      if (existing) {
        // Merge new session into existing history
        const currentHistory = existing.payload.metadata?.history;
        const updatedHistory = mergeSessionIntoHistory(currentHistory, event);
        const updatedPayload: UserFavoritePayload = {
          ...existing.payload,
          metadata: {
            ...existing.payload.metadata,
            history: updatedHistory,
          },
        };
        // Use service directly to avoid setting creating/updating/error UI state
        const updated = await userFavoritesService.updateFavorite(existing.id, updatedPayload);
        setFavorites(prev => prev.map(f => f.id === existing.id ? updated : f));
      } else {
        // Create a new record (view-only, not manually favorited)
        const history = mergeSessionIntoHistory(undefined, event);
        const kind = detectKindFromUrl(event.url);
        const input: UserFavoriteInput = {
          kind,
          payload: {
            itemId: event.url,
            title: event.title || event.url,
            url: event.url,
            metadata: {
              history,
              source: 'auto-track',
              isFavorited: false,
            },
          },
        };
        // Use service directly to avoid setting creating/updating/error UI state
        const created = await userFavoritesService.createFavorite(input);
        setFavorites(prev => [created, ...prev]);
      }
    } catch {
      // silently ignore — best-effort tracking
    }
  }, [isAuthenticated, favorites]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear favorites (local state only)
   */
  const clearFavorites = useCallback(() => {
    setFavorites([]);
    setFavoriteChecks(new Map());
    setHasMore(true);
    setContinuationToken(null);
    userFavoritesService.clearCache();
  }, []);

  // Initial load when authenticated
  useEffect(() => {
    if (isAuthenticated && !hasLoadedInitial.current) {
      hasLoadedInitial.current = true;
      loadFavorites();
    } else if (!isAuthenticated) {
      hasLoadedInitial.current = false;
      clearFavorites();
    }
  }, [isAuthenticated, loadFavorites, clearFavorites]);

  // Memoized sorted favorites (deduplicated, most recently accessed first)
  const sortedFavorites = useMemo(() => {
    const deduped = deduplicateByUrl(favorites);
    return deduped.sort((a, b) =>
      getEffectiveSortDate(b) - getEffectiveSortDate(a)
    );
  }, [favorites]);

  // Memoized insights computed from all favorites
  const insights = useMemo(() => computeInsights(favorites), [favorites]);

  // Flat list of all activity events (sessions) across all favorites
  const activityEvents = useMemo((): UserActivityEvent[] => {
    const events: UserActivityEvent[] = [];
    for (const fav of favorites) {
      const history = fav.payload?.metadata?.history as import('../types/userActivity').ResourceHistory | undefined;
      if (history?.sessions) {
        for (const session of history.sessions) {
          events.push({
            url: fav.payload.url || '',
            title: fav.payload.title,
            kind: fav.kind,
            durationMs: session.durationMs,
            openedAt: session.openedAt,
            closedAt: session.closedAt,
          });
        }
      }
    }
    events.sort((a, b) => new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime());
    return events;
  }, [favorites]);

  return {
    favorites: sortedFavorites,
    loading,
    loadingMore,
    creating,
    updating,
    error,
    hasMore,
    continuationToken,
    
    // CRUD Operations
    createFavorite,
    updateFavorite,
    deleteFavorite,
    
    // Query Operations
    loadFavorites,
    loadMore,
    refresh,
    getFavorite,
    
    // Check Operations
    checkFavorite,
    isFavorited,
    
    // Count Operations
    getCount,
    
    // Toggle Operations
    toggleFavorite,
    toggleFavoriteFlag,
    
    // Filter Operations
    filterByKind,
    
    // Helper methods
    getFavoriteById,
    getFavoriteByItemId,
    getResourceByUrl,
    
    // Activity tracking
    recordResourceVisit,
    activityEvents,
    insights,
    
    // State management
    clearError,
    clearFavorites
  };
};
